import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ScrollToTop from './components/ui/ScrollToTop';
import ProtectedRoute from './components/common/ProtectedRoute';
import LanguageRouteWrapper from './components/common/LanguageRouteWrapper';
import { useToast } from './components/ui/Toast';
// import ChatWidget from './components/chat/ChatWidget';

// Analytics
import { trackPageView } from './utils/analytics';

// CSP Debugging
import './utils/cspDebug';

// Language utilities
import { getBrowserLanguage } from './utils/languageUtils';

// Styles
import './styles/App.css';

// Pages - Lazy loaded for better performance
const Home = React.lazy(() => import('./pages/Home'));
const Menu = React.lazy(() => import('./pages/Menu'));
const MokalandMenu = React.lazy(() => import('./pages/MokalandMenu'));
const QisparkiMenu = React.lazy(() => import('./pages/QisparkiMenu'));
const AllMenu = React.lazy(() => import('./pages/AllMenu'));
const SedefMenu = React.lazy(() => import('./pages/SedefMenu'));
const BirthdayMenu = React.lazy(() => import('./pages/BirthdayMenu'));
const NewYearMenu = React.lazy(() => import('./pages/NewYearMenu'));
const About = React.lazy(() => import('./pages/About'));
const Gallery = React.lazy(() => import('./pages/Gallery'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Reservations = React.lazy(() => import('./pages/Reservations'));
const OrderOnline = React.lazy(() => import('./pages/OrderOnline'));
const Location = React.lazy(() => import('./pages/Location'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderSuccess = React.lazy(() => import('./pages/OrderSuccess'));
const TrackOrder = React.lazy(() => import('./pages/TrackOrder'));
const MenuItemDetail = React.lazy(() => import('./pages/MenuItemDetail'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// SEO Landing Pages
const Breakfast = React.lazy(() => import('./pages/Breakfast'));

// Admin Pages
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminReservations = React.lazy(() => import('./pages/admin/AdminReservations'));
const AdminMenu = React.lazy(() => import('./pages/admin/AdminMenu'));
const AdminGallery = React.lazy(() => import('./pages/admin/AdminGallery'));
const AdminContacts = React.lazy(() => import('./pages/admin/AdminContacts'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminQr = React.lazy(() => import('./pages/admin/AdminQr'));

// Page transition variants
const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.3, ease: "easeInOut" }
};

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAllMenuRoute = location.pathname.includes('/menu/all-eat');
  const { ToastRenderer } = useToast();

  // Track page views on route changes
  useEffect(() => {
    // Extract page title from pathname
    const getPageTitle = (pathname) => {
      const pathSegments = pathname.split('/').filter(Boolean);
      
      // Handle admin routes
      if (pathname.startsWith('/admin')) {
        const adminSection = pathSegments[1] || 'dashboard';
        return `Admin - ${adminSection.charAt(0).toUpperCase() + adminSection.slice(1)}`;
      }
      
      // Handle language-prefixed routes
      if (pathSegments.length > 0) {
        // Skip language prefix if present
        const isLangPrefix = pathSegments[0].length === 2; // e.g., 'en', 'az'
        const pageSegment = isLangPrefix && pathSegments[1] ? pathSegments[1] : pathSegments[0];
        
        if (!pageSegment) return 'Home';
        
        // Special case for specific routes
        const pageTitles = {
          'menu': 'Menu',
          'mokaland': 'Moka Land Menu',
          'birthday-menu': 'Birthday Menu',
          'new-year-menu': 'New Year Menu',
          'cart': 'Shopping Cart',
          'checkout': 'Checkout',
          'order-success': 'Order Success',
          'about': 'About Us',
          'gallery': 'Gallery',
          'contact': 'Contact',
          'reservations': 'Reservations',
          'order': 'Order Online',
          'location': 'Location',
          'track-order': 'Track Order'
        };
        
        return pageTitles[pageSegment] || pageSegment.charAt(0).toUpperCase() + pageSegment.slice(1);
      }
      
      return 'Home';
    };

    // Track the page view
    const pageTitle = getPageTitle(location.pathname);
    trackPageView(pageTitle, {
      page_path: location.pathname,
      page_location: window.location.href,
      is_admin: isAdminRoute,
      language: location.pathname.split('/')[1] || 'en'
    });
  }, [location, isAdminRoute]);

  // Keep the canonical link in sync with the current route.
  // The tag ships in public/index.html for the initial paint; it is updated
  // here (absolute, self-referencing) so every rendered route has one.
  // /admin/* is disallowed in robots.txt, so it gets no canonical at all.
  useEffect(() => {
    const canonical = document.querySelector('link[rel="canonical"]');

    if (isAdminRoute) {
      if (canonical) canonical.remove();
      return;
    }

    const href = `https://pizzainn.az${location.pathname}`;

    if (canonical) {
      canonical.setAttribute('href', href);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', href);
      document.head.appendChild(link);
    }
  }, [location.pathname, isAdminRoute]);

  // Root redirect component
  const RootRedirect = () => {
    const preferredLang = getBrowserLanguage();
    return <Navigate to={`/${preferredLang}`} replace />;
  };

  // Redirect component for paths without language prefix
  const PathWithoutLanguageRedirect = ({ path }) => {
    const preferredLang = getBrowserLanguage();
    return <Navigate to={`/${preferredLang}${path}`} replace />;
  };

  return (
    <div className="App">
      <ScrollToTop />
      
      {/* Header - Hidden on admin routes */}
      {!isAdminRoute && <Header />}

      {/* Main Content */}
      <main className={`main-content ${isAdminRoute ? 'admin-layout' : ''}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
            className="page-container"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <Routes location={location}>
                {/* Root redirect to default language */}
                <Route path="/" element={<RootRedirect />} />
                
                {/* SEO Landing Pages - Direct routes for high-volume search terms */}
                <Route path="/breakfast" element={<Breakfast />} />
                
                {/* Redirect paths without language prefix */}
                <Route path="/menu" element={<PathWithoutLanguageRedirect path="/menu" />} />
                <Route path="/menu/:id" element={<PathWithoutLanguageRedirect path="/menu" />} />
                <Route path="/menu/qis-parki" element={<PathWithoutLanguageRedirect path="/menu/qis-parki" />} />
                <Route path="/menu/all-eat" element={<PathWithoutLanguageRedirect path="/menu/all-eat" />} />
                <Route path="/menu/sedef" element={<PathWithoutLanguageRedirect path="/menu/sedef" />} />
                <Route path="/mokaland" element={<PathWithoutLanguageRedirect path="/mokaland" />} />
                <Route path="/mokaland/menu" element={<PathWithoutLanguageRedirect path="/mokaland/menu" />} />
                <Route path="/mokaland/birthday-menu" element={<PathWithoutLanguageRedirect path="/mokaland/birthday-menu" />} />
                <Route path="/mokaland/new-year-menu" element={<PathWithoutLanguageRedirect path="/mokaland/new-year-menu" />} />
                <Route path="/about" element={<PathWithoutLanguageRedirect path="/about" />} />
                <Route path="/gallery" element={<PathWithoutLanguageRedirect path="/gallery" />} />
                <Route path="/contact" element={<PathWithoutLanguageRedirect path="/contact" />} />
                <Route path="/reservations" element={<PathWithoutLanguageRedirect path="/reservations" />} />
                <Route path="/order" element={<PathWithoutLanguageRedirect path="/order" />} />
                <Route path="/location" element={<PathWithoutLanguageRedirect path="/location" />} />
                <Route path="/cart" element={<PathWithoutLanguageRedirect path="/cart" />} />
                <Route path="/checkout" element={<PathWithoutLanguageRedirect path="/checkout" />} />
                <Route path="/order-success" element={<PathWithoutLanguageRedirect path="/order-success" />} />
                <Route path="/track-order/:id" element={<PathWithoutLanguageRedirect path="/track-order" />} />
                
                {/* Language-based routes */}
                <Route path="/:lang/*" element={
                  <LanguageRouteWrapper>
                    <Routes>
                      {/* Public Routes with language prefix */}
                      <Route path="/" element={<Home />} />
                      <Route path="/menu" element={<Menu />} />
                      <Route path="/menu/:id" element={<MenuItemDetail />} />
                      <Route path="/menu/qis-parki" element={<QisparkiMenu />} />
                      <Route path="/menu/all-eat" element={<AllMenu />} />
                      <Route path="/menu/sedef" element={<SedefMenu />} />
                      <Route path="/mokaland" element={<MokalandMenu />} />
                      <Route path="/mokaland/menu" element={<MokalandMenu />} />
                      <Route path="/mokaland/birthday-menu" element={<BirthdayMenu />} />
                      <Route path="/mokaland/new-year-menu" element={<NewYearMenu />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/gallery" element={<Gallery />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/reservations" element={<Reservations />} />
                      <Route path="/order" element={<OrderOnline />} />
                      <Route path="/location" element={<Location />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-success" element={<OrderSuccess />} />
                      <Route path="/track-order/:id" element={<TrackOrder />} />
                      
                      {/* 404 Route for language routes */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </LanguageRouteWrapper>
                } />
                
                {/* Admin Routes (no language prefix) */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/dashboard" element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/orders" element={
                  <ProtectedRoute>
                    <AdminOrders />
                  </ProtectedRoute>
                } />
                <Route path="/admin/reservations" element={
                  <ProtectedRoute>
                    <AdminReservations />
                  </ProtectedRoute>
                } />
                <Route path="/admin/menu" element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <AdminMenu />
                  </ProtectedRoute>
                } />
                <Route path="/admin/gallery" element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <AdminGallery />
                  </ProtectedRoute>
                } />
                <Route path="/admin/contacts" element={
                  <ProtectedRoute>
                    <AdminContacts />
                  </ProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <AdminSettings />
                  </ProtectedRoute>
                } />
                <Route path="/admin/qr" element={
                  <ProtectedRoute>
                    <AdminQr />
                  </ProtectedRoute>
                } />
                
                {/* 404 Route for non-language routes */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      
      {/* Footer - Hidden on admin and ALL restaurant routes (AllMenu has its own footer) */}
      {!isAdminRoute && !isAllMenuRoute && <Footer />}
      
      {/* AI Chat Widget - Hidden on admin routes */}
      {/* {!isAdminRoute && <ChatWidget />} */}
      
      {/* Toast Notifications */}
      <ToastRenderer />
    </div>
  );
}

export default App;
